import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { 
  Save, 
  X, 
  Plus, 
  Trash2, 
  GripVertical,
  CheckCircle2,
  Circle,
  Calendar,
  User,
  Flag
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface Todo {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  assignee?: string;
  order: number;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: 'planning' | 'in-progress' | 'completed' | 'on-hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  tags: string[];
  todos: Todo[];
  createdAt: Date;
  updatedAt: Date;
}

const TodoItem: React.FC<{
  todo: Todo;
  onUpdate: (todo: Todo) => void;
  onDelete: (id: string) => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  index: number;
}> = ({ todo, onUpdate, onDelete, onMove, index }) => {
  const { colors } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(todo);

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'todo',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'todo',
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
        item.index = index;
      }
    },
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return colors.red;
      case 'high': return colors.peach;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const handleSave = () => {
    onUpdate(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(todo);
    setIsEditing(false);
  };

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`p-4 rounded-lg border transition-all ${isDragging ? 'opacity-50' : 'hover:opacity-80'}`}
      style={{ 
        backgroundColor: colors.base,
        borderColor: colors.surface1
      }}
    >
      {isEditing ? (
        <div className="space-y-3">
          <input
            type="text"
            value={editData.title}
            onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 rounded border"
            style={{
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
            placeholder="Todo title..."
          />
          
          <textarea
            value={editData.description}
            onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 rounded border resize-none"
            rows={2}
            style={{
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
            placeholder="Description..."
          />
          
          <div className="flex items-center gap-4">
            <select
              value={editData.priority}
              onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value as any }))}
              className="px-2 py-1 rounded border text-sm"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            
            <input
              type="date"
              value={editData.dueDate || ''}
              onChange={(e) => setEditData(prev => ({ ...prev, dueDate: e.target.value }))}
              className="px-2 py-1 rounded border text-sm"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 rounded text-sm font-medium text-white"
              style={{ backgroundColor: colors.green }}
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1 rounded text-sm border"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <GripVertical className="w-4 h-4 mt-1 cursor-move" style={{ color: colors.subtext0 }} />
          
          <button
            onClick={() => onUpdate({ ...todo, completed: !todo.completed })}
            className="mt-1"
          >
            {todo.completed ? (
              <CheckCircle2 className="w-5 h-5" style={{ color: colors.green }} />
            ) : (
              <Circle className="w-5 h-5" style={{ color: colors.subtext0 }} />
            )}
          </button>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 
                className={`font-medium ${todo.completed ? 'line-through opacity-60' : ''}`}
                style={{ color: colors.text }}
              >
                {todo.title}
              </h4>
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: getPriorityColor(todo.priority) }}
              />
            </div>
            
            {todo.description && (
              <p 
                className={`text-sm mb-2 ${todo.completed ? 'line-through opacity-60' : ''}`}
                style={{ color: colors.subtext0 }}
              >
                {todo.description}
              </p>
            )}
            
            <div className="flex items-center gap-4 text-xs" style={{ color: colors.subtext0 }}>
              {todo.dueDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(todo.dueDate).toLocaleDateString()}
                </div>
              )}
              
              <div className="flex items-center gap-1">
                <Flag className="w-3 h-3" />
                {todo.priority}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 rounded hover:opacity-80"
              style={{ color: colors.subtext0 }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            
            <button
              onClick={() => onDelete(todo.id)}
              className="p-1 rounded hover:opacity-80"
              style={{ color: colors.red }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectEditor: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [project, setProject] = useState<Project>({
    id: '',
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    dueDate: '',
    tags: [],
    todos: [],
    createdAt: new Date(),
    updatedAt: new Date()
  });

  const [newTag, setNewTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      // Load existing project
      const mockProject: Project = {
        id: id!,
        name: 'React Learning Path',
        description: 'Complete React.js mastery course with practical projects',
        status: 'in-progress',
        priority: 'high',
        dueDate: '2025-02-15',
        tags: ['React', 'Frontend', 'Learning'],
        todos: [
          {
            id: '1',
            title: 'Learn React Basics',
            description: 'Components, JSX, Props, State',
            completed: true,
            priority: 'high',
            dueDate: '2025-01-20',
            order: 0
          },
          {
            id: '2',
            title: 'Master React Hooks',
            description: 'useState, useEffect, useContext, custom hooks',
            completed: false,
            priority: 'high',
            dueDate: '2025-01-25',
            order: 1
          },
          {
            id: '3',
            title: 'Build Todo App',
            description: 'Practice with a complete project',
            completed: false,
            priority: 'medium',
            dueDate: '2025-02-01',
            order: 2
          }
        ],
        createdAt: new Date('2025-01-15'),
        updatedAt: new Date()
      };
      setProject(mockProject);
    }
  }, [id, isEditing]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedProject = {
        ...project,
        id: project.id || Date.now().toString(),
        updatedAt: new Date()
      };
      
      setProject(updatedProject);
      console.log('Project saved:', updatedProject);
      
      if (!isEditing) {
        navigate(`/projects/${updatedProject.id}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTodo = () => {
    const newTodo: Todo = {
      id: Date.now().toString(),
      title: 'New Todo',
      description: '',
      completed: false,
      priority: 'medium',
      order: project.todos.length
    };
    
    setProject(prev => ({
      ...prev,
      todos: [...prev.todos, newTodo]
    }));
  };

  const handleUpdateTodo = (updatedTodo: Todo) => {
    setProject(prev => ({
      ...prev,
      todos: prev.todos.map(todo => 
        todo.id === updatedTodo.id ? updatedTodo : todo
      )
    }));
  };

  const handleDeleteTodo = (todoId: string) => {
    setProject(prev => ({
      ...prev,
      todos: prev.todos.filter(todo => todo.id !== todoId)
    }));
  };

  const handleMoveTodo = (dragIndex: number, hoverIndex: number) => {
    const dragTodo = project.todos[dragIndex];
    const newTodos = [...project.todos];
    newTodos.splice(dragIndex, 1);
    newTodos.splice(hoverIndex, 0, dragTodo);
    
    setProject(prev => ({
      ...prev,
      todos: newTodos.map((todo, index) => ({ ...todo, order: index }))
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !project.tags.includes(newTag.trim())) {
      setProject(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setProject(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const completedTodos = project.todos.filter(todo => todo.completed).length;
  const progress = project.todos.length > 0 ? (completedTodos / project.todos.length) * 100 : 0;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-[calc(100vh-8rem)] flex flex-col">
        {/* Header */}
        <div 
          className="flex items-center justify-between p-4 border-b"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <div className="flex items-center gap-4 flex-1">
            <input
              type="text"
              value={project.name}
              onChange={(e) => setProject(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Project name..."
              className="text-xl font-semibold bg-transparent border-none outline-none flex-1"
              style={{ color: colors.text }}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: colors.blue }}
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            
            <button
              onClick={() => navigate('/projects')}
              className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80"
              style={{ 
                backgroundColor: colors.base,
                borderColor: colors.surface0,
                color: colors.text
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Project Details */}
        <div 
          className="p-4 border-b"
          style={{ 
            backgroundColor: colors.base,
            borderColor: colors.surface0
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text }}>
                Status
              </label>
              <select
                value={project.status}
                onChange={(e) => setProject(prev => ({ ...prev, status: e.target.value as any }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0,
                  color: colors.text
                }}
              >
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text }}>
                Priority
              </label>
              <select
                value={project.priority}
                onChange={(e) => setProject(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0,
                  color: colors.text
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.text }}>
                Due Date
              </label>
              <input
                type="date"
                value={project.dueDate}
                onChange={(e) => setProject(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full px-3 py-2 rounded border"
                style={{
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface0,
                  color: colors.text
                }}
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: colors.text }}>
              Description
            </label>
            <textarea
              value={project.description}
              onChange={(e) => setProject(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Project description..."
              className="w-full px-3 py-2 rounded border resize-none"
              rows={3}
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
          
          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: colors.text }}>
                Progress
              </span>
              <span className="text-sm" style={{ color: colors.subtext0 }}>
                {completedTodos}/{project.todos.length} tasks ({Math.round(progress)}%)
              </span>
            </div>
            <div 
              className="w-full h-2 rounded-full"
              style={{ backgroundColor: colors.surface0 }}
            >
              <div 
                className="h-2 rounded-full transition-all"
                style={{ 
                  backgroundColor: colors.blue,
                  width: `${progress}%`
                }}
              />
            </div>
          </div>
          
          {/* Tags */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium" style={{ color: colors.text }}>Tags:</span>
            
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 rounded flex items-center gap-1"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.text
                  }}
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:opacity-80"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Add tag..."
                className="text-sm px-2 py-1 rounded border"
                style={{
                  backgroundColor: colors.mantle,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              <button
                onClick={handleAddTag}
                className="text-sm px-3 py-1 rounded font-medium text-white"
                style={{ backgroundColor: colors.blue }}
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Todos Section */}
        <div className="flex-1 overflow-y-auto">
          <div 
            className="p-4 border-b flex items-center justify-between"
            style={{ borderColor: colors.surface0 }}
          >
            <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
              Tasks ({project.todos.length})
            </h3>
            
            <button
              onClick={handleAddTodo}
              className="px-3 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: colors.green }}
            >
              <Plus className="w-4 h-4" />
              Add Task
            </button>
          </div>
          
          <div className="p-4 space-y-3">
            {project.todos
              .sort((a, b) => a.order - b.order)
              .map((todo, index) => (
                <TodoItem
                  key={todo.id}
                  todo={todo}
                  index={index}
                  onUpdate={handleUpdateTodo}
                  onDelete={handleDeleteTodo}
                  onMove={handleMoveTodo}
                />
              ))}
            
            {project.todos.length === 0 && (
              <div className="text-center py-12">
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: colors.surface0 }}
                >
                  <CheckCircle2 className="w-8 h-8" style={{ color: colors.subtext0 }} />
                </div>
                <h3 className="text-lg font-medium mb-2" style={{ color: colors.text }}>
                  No tasks yet
                </h3>
                <p className="mb-4" style={{ color: colors.subtext0 }}>
                  Add your first task to get started
                </p>
                <button
                  onClick={handleAddTodo}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: colors.blue }}
                >
                  Add Task
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default ProjectEditor;